import { useAuth } from '@clerk/react';
import { useCopilotRuntimeOptional } from '@/components/agent-chat/copilot-runtime-provider.component';
import {
  fetchAgentChats,
  fetchAgentsChats,
  type AgentChat,
} from '@/containers/agents/use-agent-chats.hook';
import {
  useChatTitles,
  withChatTitles,
  type RenamedMark,
} from '@/hooks/chat-titles.store';
import { NO_LOCAL_CHATS, type LocalChats } from '@/hooks/local-chats.util';
import type { ConversationSummary } from '@/hooks/use-agent-chat.hook';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { useCopilotAccess } from '@/hooks/use-copilot-access.hook';
import { useListAgentsQuery } from '@/store/features/gtm-agents.feature';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CHAT_TABS_LIMIT, recentAgentIds } from './chat-tabs.util';

/** Asks every reader of the chat lists to read the agents' chats again. */
export const CHATS_CHANGED_EVENT = 'cleanlist:chats-changed';

/** A chat was deleted or changed outside a turn: the lists read again. */
export function announceChatsChanged(): void {
  window.dispatchEvent(new Event(CHATS_CHANGED_EVENT));
}

const NO_CHATS: readonly ConversationSummary[] = [];

/**
 * Each agent's chats, settled per agent in `ids` order: one batched read
 * (CLEANLIST-PORTAL-93), or one read per agent from an API that predates
 * `agent_ids`. A failed batched read fails every agent.
 */
async function readAgentLists(
  ids: readonly string[],
  scope: object
): Promise<PromiseSettledResult<AgentChat[]>[]> {
  if (ids.length === 0) return [];
  let batch: AgentChat[] | null;
  try {
    batch = await fetchAgentsChats(ids, CHAT_TABS_LIMIT, scope);
  } catch (reason) {
    return ids.map(() => ({ status: 'rejected', reason }));
  }
  if (batch) {
    const chats = batch;
    return ids.map((agentId) => ({
      status: 'fulfilled',
      value: chats.filter((chat) => chat.context_agent_id === agentId),
    }));
  }
  return Promise.allSettled(
    ids.map(async (agentId) =>
      (await fetchAgentChats(agentId, CHAT_TABS_LIMIT, scope)).map((chat) => ({
        ...chat,
        context_agent_id: chat.context_agent_id ?? agentId,
      }))
    )
  );
}

// Tabs and the rail read the same runtime. Share their task-level reads while
// keeping a new auth scope separate from any old in-flight requests.
const agentReadScopes = new WeakMap<
  object,
  { authScope: string; scope: object }
>();

export interface ChatLists {
  /** Clu may be used here (not an AppSumo-only org). */
  available: boolean;
  /** Clu's chats: the shared runtime's history. */
  cluChats: readonly (ConversationSummary & RenamedMark)[];
  /** The chats of the agents most recently modified, each with its agent. */
  agentChats: readonly (AgentChat & RenamedMark)[];
  agentNames: ReadonlyMap<string, string>;
  /** When each agent's greeting started, for the agents Clu greeted. */
  agentGreetings: ReadonlyMap<string, string>;
  /** Every list has been read once, for the agents known now. */
  listsRead: boolean;
  /**
   * Every list has come back once. It stays true while later reads come
   * back (an agent becomes recent, Agents access resolves late), so the
   * lists are not drawn as loading again.
   */
  loaded: boolean;
  failed: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
  /** What the runtime knows before the lists: see local-chats.util. */
  local: LocalChats;
}

/**
 * The user's chats as the sidebar's tabs and the Chats rail read them.
 * Clu's chats are the shared runtime's history (`GET /agent/conversations`),
 * read as this mounts and again by the runtime after each turn; the agents'
 * own chats are read in one request by their ids, since the general list
 * leaves them out, and only for the agents most recently modified. Both
 * carry the titles users gave their chats (see
 * chat-titles.store), from the moment they give them.
 */
export function useChatLists(): ChatLists {
  const runtime = useCopilotRuntimeOptional();
  const { orgId, userId } = useAuth();
  const authScope = JSON.stringify([orgId, userId]);
  const { canUseCopilot } = useCopilotAccess();
  const titles = useChatTitles();

  const runtimeScopeCurrent =
    runtime?.historyScope === undefined || runtime.historyScope === authScope;
  const listedCluChats = runtimeScopeCurrent
    ? (runtime?.conversations ?? NO_CHATS)
    : NO_CHATS;
  const cluChats = useMemo(
    () => withChatTitles(listedCluChats, titles),
    [listedCluChats, titles]
  );
  const fetchConversations = runtime?.fetchConversations;
  const agentReadScope = useMemo(() => {
    if (!fetchConversations) return {};
    const previous = agentReadScopes.get(fetchConversations);
    if (previous?.authScope === authScope) return previous.scope;
    const scope = {};
    agentReadScopes.set(fetchConversations, { authScope, scope });
    return scope;
  }, [fetchConversations, authScope]);
  const [cluReadScope, setCluReadScope] = useState<string | null>(null);
  const cluRead = cluReadScope === authScope;
  useEffect(() => {
    if (!canUseCopilot || !fetchConversations || !runtimeScopeCurrent) return;
    let active = true;
    void Promise.resolve(fetchConversations())
      .catch(() => undefined)
      .finally(() => {
        if (active) setCluReadScope(authScope);
      });
    return () => {
      active = false;
    };
  }, [canUseCopilot, fetchConversations, authScope, runtimeScopeCurrent]);

  const { unlocked: agentsUnlocked } = useAgentsAccess();
  // Unscoped: the same cache entry as the nav's agents (useTabsNav) and the
  // agents pages, so the sidebar reads the list once. A scoped arg is a
  // second entry and a second GET /gtm-agents on every page; an org switch
  // reloads the app, so no other org's list can be cached here.
  const {
    currentData: agents,
    isError: agentsReadFailed,
    error: agentsError,
    isFetching: agentsLoading,
    refetch: refetchAgents,
  } = useListAgentsQuery(undefined, {
    skip: !orgId || !agentsUnlocked || !canUseCopilot,
  });
  // A 403 is the server saying the plan has no agents (the plan just changed,
  // or the dev billing simulator says otherwise): no agent chats, not a
  // failed read, so the rail doesn't say chats couldn't load.
  const agentsRefused =
    agentsReadFailed &&
    typeof agentsError === 'object' &&
    agentsError !== null &&
    'status' in agentsError &&
    agentsError.status === 403;
  const agentsFailed = agentsReadFailed && !agentsRefused;
  const agentIds = useMemo(
    () => recentAgentIds(agents ?? []).join(','),
    [agents]
  );
  const agentNames = useMemo(
    () => new Map((agents ?? []).map((agent) => [agent.id, agent.name])),
    [agents]
  );
  const agentGreetings = useMemo(
    () =>
      new Map(
        (agents ?? []).flatMap((agent) =>
          agent.greetedAt ? [[agent.id, agent.greetedAt] as const] : []
        )
      ),
    [agents]
  );
  const [agentChats, setAgentChats] = useState<AgentChat[]>([]);
  // The agents whose chats the last completed read covered.
  const [agentChatsReadFor, setAgentChatsReadFor] = useState<string | null>(
    null
  );
  const [agentFailed, setAgentFailed] = useState(false);
  const [agentLoading, setAgentLoading] = useState(false);
  const scope = JSON.stringify([
    authScope,
    canUseCopilot,
    agentsUnlocked,
    agentIds,
  ]);
  const epoch = useRef({
    authScope,
    scope,
    fetchConversations,
    pending: null as Promise<void> | null,
  });
  if (
    epoch.current.scope !== scope ||
    epoch.current.fetchConversations !== fetchConversations
  ) {
    if (
      epoch.current.authScope !== authScope ||
      epoch.current.fetchConversations !== fetchConversations
    ) {
      setAgentChats([]);
      setCluReadScope(null);
    }
    epoch.current = { authScope, scope, fetchConversations, pending: null };
    setAgentFailed(false);
    setAgentLoading(false);
    setAgentChatsReadFor(null);
  }
  const origin = epoch.current;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const settleAgentChats = runtime?.settleAgentChats;
  const refetchAgentChats = useCallback((): Promise<void> => {
    if (
      !mounted.current ||
      !orgId ||
      !userId ||
      epoch.current !== origin ||
      !canUseCopilot
    )
      return Promise.resolve();
    if (origin.pending) return origin.pending;
    const active = () => mounted.current && epoch.current === origin;
    const ids = agentsUnlocked && agentIds ? agentIds.split(',') : [];
    setAgentLoading(true);
    const request = (async () => {
      const lists = await readAgentLists(ids, agentReadScope);
      if (!active()) return;
      const chats = lists.flatMap((result) =>
        result.status === 'fulfilled' ? result.value : []
      );
      const failedIds = new Set(
        ids.filter((_, index) => lists[index].status === 'rejected')
      );
      setAgentChats((previous) => [
        ...chats,
        ...previous.filter((chat) =>
          failedIds.has(chat.context_agent_id ?? '')
        ),
      ]);
      setAgentFailed(failedIds.size > 0);
      setAgentLoading(false);
      setAgentChatsReadFor(agentIds);
      settleAgentChats?.(
        new Set(chats.map((chat) => chat.id)),
        new Set(ids.filter((id) => !failedIds.has(id)))
      );
    })().finally(() => {
      origin.pending = null;
    });
    origin.pending = request;
    return request;
  }, [
    agentIds,
    agentReadScope,
    agentsUnlocked,
    canUseCopilot,
    origin,
    settleAgentChats,
    orgId,
    userId,
  ]);
  useEffect(() => {
    void refetchAgentChats();
  }, [refetchAgentChats]);
  useEffect(() => {
    const again = () => void refetchAgentChats();
    window.addEventListener(CHATS_CHANGED_EVENT, again);
    return () => window.removeEventListener(CHATS_CHANGED_EVENT, again);
  }, [refetchAgentChats]);
  const refetch = useCallback(async () => {
    await Promise.allSettled([
      Promise.resolve(fetchConversations?.()),
      refetchAgentChats(),
      ...(agentsFailed && refetchAgents
        ? [Promise.resolve(refetchAgents())]
        : []),
    ]);
  }, [fetchConversations, refetchAgentChats, agentsFailed, refetchAgents]);

  // Again when a turn ends or a chat starts outside Clu's own listed chats,
  // which is where an agent's chat moves.
  const local = runtimeScopeCurrent
    ? (runtime?.localChats ?? NO_LOCAL_CHATS)
    : NO_LOCAL_CHATS;
  const streaming = runtime?.isStreaming ?? false;
  const conversationId = runtime?.conversationId ?? null;
  const settled = useRef({ streaming, conversationId });
  const cluIds = useMemo(
    () => new Set(cluChats.map((chat) => chat.id)),
    [cluChats]
  );
  const startedCluChat =
    local.pending !== null &&
    local.pending.id === conversationId &&
    local.pending.agentId === null;
  useEffect(() => {
    const before = settled.current;
    settled.current = { streaming, conversationId };
    const turnEnded = before.streaming && !streaming;
    const chatStarted =
      Boolean(conversationId) && conversationId !== before.conversationId;
    if (!turnEnded && !chatStarted) return;
    if (conversationId && cluIds.has(conversationId)) return;
    // A Clu chat named mid-turn is no agent's: the turn's end reads again.
    if (!turnEnded && startedCluChat) return;
    void refetchAgentChats();
  }, [streaming, conversationId, cluIds, refetchAgentChats, startedCluChat]);

  // A failed read settles loading while the recovery notice remains visible.
  const agentsKnown =
    !agentsUnlocked || agents !== undefined || agentsReadFailed;
  const listsRead = cluRead && agentsKnown && agentChatsReadFor === agentIds;
  const [loadedScope, setLoadedScope] = useState<string | null>(null);
  const loaded = loadedScope === authScope;
  if (listsRead && !loaded) setLoadedScope(authScope);
  const titledAgentChats = useMemo(
    () => withChatTitles(agentChats, titles),
    [agentChats, titles]
  );
  return {
    available: canUseCopilot,
    cluChats,
    agentChats: titledAgentChats,
    agentNames,
    agentGreetings,
    listsRead,
    loaded: loaded || listsRead,
    failed:
      canUseCopilot &&
      Boolean(
        (runtimeScopeCurrent && runtime?.historyFailed) ||
        agentsFailed ||
        agentFailed
      ),
    loading: Boolean(
      (runtimeScopeCurrent && runtime?.isLoadingHistory) ||
      agentsLoading ||
      agentLoading
    ),
    refetch,
    local,
  };
}
